"use client"
import {
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    Button,
    Box,
    Stack,
    Typography,
    Divider,
    Card,
    Grid,
    Chip,
} from "@mui/joy"
import CloseIcon from "@mui/icons-material/Close"
import moment from "moment"

export default function FareRulesModal({ open, onClose, fareData }) {
    if (!fareData) return null

    // Helper to format date if needed
    const formatDate = (date) => moment(date).format("DD MMM, YY")

    // Check for Hitit structure (has `remarks` array from the mapper)
    if (fareData.remarks !== undefined) {
        const remarks = fareData.remarks || [];
        return (
            <Modal open={open} onClose={onClose}>
                <ModalDialog
                    aria-labelledby="fare-modal-title"
                    size="lg"
                    sx={{
                        width: 680,
                        maxWidth: "95vw",
                        borderRadius: "md",
                        boxShadow: "lg",
                    }}
                >
                    <DialogTitle id="fare-modal-title" sx={{ pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <Box>
                                <Typography level="h2" sx={{ fontSize: "lg", fontWeight: "lg" }}>
                                    Fare Summary & Rules
                                </Typography>
                                {fareData.airlineCode && (
                                    <Typography level="body-sm" sx={{ color: "neutral.500", mt: 0.25 }}>
                                        Airline: {fareData.airlineCode}
                                    </Typography>
                                )}
                            </Box>
                            <Button
                                variant="plain"
                                color="neutral"
                                size="sm"
                                onClick={onClose}
                                sx={{ borderRadius: "50%", minWidth: "auto" }}
                            >
                                <CloseIcon />
                            </Button>
                        </Box>
                    </DialogTitle>

                    <Divider sx={{ my: 1 }} />

                    <DialogContent sx={{ maxHeight: "72vh", overflowY: "auto", px: 3 }}>
                        <Stack spacing={2}>
                            <Typography
                                level="h3"
                                sx={{
                                    fontSize: "sm",
                                    fontWeight: "bold",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    opacity: 0.65,
                                    mb: 0.5,
                                }}
                            >
                                Fare Rules &amp; Conditions
                            </Typography>

                            {remarks.length > 0 ? (
                                remarks.map((rule, idx) => {
                                    const isPenalty = ["16", "31", "33"].includes(rule?.ruleCode);
                                    const isBaggage = rule?.ruleCode === "23";
                                    const borderColor = isPenalty
                                        ? "var(--joy-palette-warning-400, #d97706)"
                                        : isBaggage
                                            ? "var(--joy-palette-primary-400, #2563eb)"
                                            : "var(--joy-palette-neutral-300, #9ca3af)";
                                    const chipColor = isPenalty ? "warning" : isBaggage ? "primary" : "neutral";

                                    return (
                                        <Card
                                            key={idx}
                                            variant="outlined"
                                            sx={{
                                                borderLeft: `4px solid ${borderColor}`,
                                                backgroundColor: "var(--joy-palette-background-level1)",
                                                gap: 1,
                                            }}
                                        >
                                            {/* Header row: badge + title */}
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Chip size="sm" variant="solid" color={chipColor}>
                                                    {rule?.ruleCode || "—"}
                                                </Chip>
                                                <Typography level="body-md" sx={{ fontWeight: "bold", color: "text.primary" }}>
                                                    {rule?.title || "Rule"}
                                                </Typography>
                                            </Box>

                                            {/* Rule text */}
                                            {rule?.text && (
                                                <Typography
                                                    level="body-xs"
                                                    sx={{
                                                        whiteSpace: "pre-wrap",
                                                        lineHeight: 1.6,
                                                        color: "text.secondary",
                                                        backgroundColor: "var(--joy-palette-background-surface)",
                                                        borderRadius: "sm",
                                                        p: 1.5,
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                        fontFamily: "inherit",
                                                    }}
                                                >
                                                    {rule.text}
                                                </Typography>
                                            )}
                                        </Card>
                                    );
                                })
                            ) : (
                                <Box
                                    sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: 1,
                                        backgroundColor: "success.softBg",
                                        border: "1px solid",
                                        borderColor: "success.outlinedBorder",
                                        borderRadius: "md",
                                        px: 2,
                                        py: 1.5,
                                    }}
                                >
                                    <Typography level="body-sm" sx={{ fontWeight: "bold", color: "success.700" }}>
                                        ✓ No Restrictions Found
                                    </Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>

                    <Divider />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <Button variant="plain" color="neutral" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        );
    }

    // Check for Amadeus Detailed Fare Rules structure
    const amadeusDetailedRules = fareData?.included?.["detailed-fare-rules"];
    if (amadeusDetailedRules) {
        const rulesArray = Object.values(amadeusDetailedRules);
        return (
            <Modal open={open} onClose={onClose}>
                <ModalDialog
                    aria-labelledby="fare-modal-title"
                    size="lg"
                    sx={{
                        width: 800,
                        maxWidth: "95vw",
                        borderRadius: "md",
                        boxShadow: "lg",
                    }}
                >
                    <DialogTitle id="fare-modal-title" sx={{ pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <Box>
                                <Typography level="h2" sx={{ fontSize: "lg", fontWeight: "lg" }}>
                                    Amadeus Fare Rules & Conditions
                                </Typography>
                            </Box>
                            <Button
                                variant="plain"
                                color="neutral"
                                size="sm"
                                onClick={onClose}
                                sx={{ borderRadius: "50%", minWidth: "auto" }}
                            >
                                <CloseIcon />
                            </Button>
                        </Box>
                    </DialogTitle>

                    <Divider sx={{ my: 1 }} />

                    <DialogContent sx={{ maxHeight: "75vh", overflowY: "auto", px: 2 }}>
                        <Stack spacing={3}>
                            {rulesArray.length > 0 ? (
                                rulesArray.map((rule, idx) => (
                                    <Box key={idx}>
                                        <Card
                                            variant="soft"
                                            color="primary"
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                borderLeft: "4px solid var(--joy-palette-primary-500)",
                                                backgroundColor: "var(--joy-palette-primary-softBg)"
                                            }}
                                        >
                                            <Typography level="title-md" sx={{ fontWeight: "bold" }}>
                                                {rule?.name || "Fare Rules"}
                                            </Typography>
                                            <Typography level="body-xs" sx={{ mt: 0.5 }}>
                                                Fare Basis: <b>{rule?.fareBasis || "N/A"}</b>
                                            </Typography>
                                        </Card>

                                        <Stack spacing={2}>
                                            {rule?.fareNotes?.descriptions?.map((desc, dIdx) => (
                                                <Card
                                                    key={dIdx}
                                                    variant="outlined"
                                                    sx={{
                                                        p: 2,
                                                        backgroundColor: "var(--joy-palette-background-level1)"
                                                    }}
                                                >
                                                    <Typography
                                                        level="title-sm"
                                                        color="warning"
                                                        sx={{
                                                            mb: 1.5,
                                                            textTransform: "uppercase",
                                                            fontSize: "xs",
                                                            fontWeight: "bold",
                                                            letterSpacing: "0.1em"
                                                        }}
                                                    >
                                                        {desc.descriptionType}
                                                    </Typography>
                                                    <Typography
                                                        level="body-xs"
                                                        sx={{
                                                            whiteSpace: "pre-wrap",
                                                            fontFamily: "monospace",
                                                            fontSize: "11px",
                                                            lineHeight: 1.6,
                                                            color: "text.secondary",
                                                            backgroundColor: "var(--joy-palette-background-surface)",
                                                            p: 1.5,
                                                            borderRadius: "sm",
                                                            border: "1px solid",
                                                            borderColor: "divider"
                                                        }}
                                                    >
                                                        {desc.text}
                                                    </Typography>
                                                </Card>
                                            ))}
                                        </Stack>
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{ p: 4, textAlign: "center" }}>
                                    <Typography color="neutral">No detailed fare rules found for this selection.</Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>

                    <Divider />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                        <Button variant="solid" color="primary" onClick={onClose} sx={{ minWidth: 100 }}>
                            Close
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        )
    }

    // Check for older Amadeus structure (has `rules` array)
    if (fareData.rules !== undefined) {
        const rules = fareData.rules || [];
        return (
            <Modal open={open} onClose={onClose}>
                <ModalDialog
                    aria-labelledby="fare-modal-title"
                    size="lg"
                    sx={{
                        width: 600,
                        maxWidth: "95vw",
                        borderRadius: "md",
                        boxShadow: "lg",
                    }}
                >
                    <DialogTitle id="fare-modal-title" sx={{ pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <Typography level="h2" sx={{ fontSize: "lg", fontWeight: "lg" }}>
                                Fare Summary & Rules
                            </Typography>
                            <Button
                                variant="plain"
                                color="neutral"
                                size="sm"
                                onClick={onClose}
                                sx={{ borderRadius: "50%", minWidth: "auto" }}
                            >
                                <CloseIcon />
                            </Button>
                        </Box>
                    </DialogTitle>
                    <Divider sx={{ my: 1 }} />
                    <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", px: 3 }}>
                        <Stack spacing={2}>
                            {rules.length > 0 ? (
                                rules.map((rule, idx) => (
                                    <Card key={idx} variant="outlined" sx={{ p: 2, borderLeft: "4px solid var(--joy-palette-primary-500)" }}>
                                        <Typography level="title-sm" color="primary" sx={{ fontWeight: "bold" }}>
                                            Category: {rule?.category || "N/A"}
                                        </Typography>
                                        <Typography level="body-sm" sx={{ mt: 1 }}>
                                            For this category, the penalty amount is <b>RS.{rule?.maxPenaltyAmount || "0"}</b>
                                        </Typography>
                                    </Card>
                                ))
                            ) : (
                                <Box sx={{ p: 2, textAlign: "center" }}>
                                    <Typography color="neutral">No fare rules found.</Typography>
                                </Box>
                            )}
                        </Stack>
                    </DialogContent>
                    <Divider />
                    <Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
                        <Button variant="plain" color="neutral" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        );
    }

    // Check for NDC structure (FareRules.RuleDetails)
    if (fareData?.FareRules?.RuleDetails) {
        return (
            <Modal open={open} onClose={onClose}>
                <ModalDialog
                    aria-labelledby="fare-modal-title"
                    size="lg"
                    sx={{
                        width: 600,
                        maxWidth: "95vw",
                        borderRadius: "md",
                        boxShadow: "lg",
                    }}
                >
                    <DialogTitle id="fare-modal-title" sx={{ pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <Typography level="h2" sx={{ fontSize: "lg", fontWeight: "lg" }}>
                                Fare Summary & Rules
                            </Typography>
                            <Button
                                variant="plain"
                                color="neutral"
                                size="sm"
                                onClick={onClose}
                                sx={{ borderRadius: "50%", minWidth: "auto" }}
                            >
                                <CloseIcon />
                            </Button>
                        </Box>
                    </DialogTitle>

                    <Divider sx={{ my: 1 }} />

                    <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", px: 3 }}>
                        <Stack spacing={2} sx={{ py: 1 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: "700", color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.8px", mb: "2px" }}>
                                Penalties &amp; Conditions
                            </Typography>
                            {fareData.FareRules.RuleDetails.map((item, index) => {
                                const text = item.Text || "";
                                const isExchange = text.toLowerCase().includes("reissue") || text.toLowerCase().includes("revalidation") || text.toLowerCase().includes("change");
                                const isRefund = !isExchange && text.toLowerCase().includes("refund");
                                const isBefore = text.toLowerCase().includes("before departure");
                                const isAfter = text.toLowerCase().includes("after departure");

                                // Simple parsing for amount and currency
                                const amountMatch = text.match(/Penalty: ([\d\.]+) ([A-Z]{3})/);
                                const amount = amountMatch ? amountMatch[1] : null;
                                const currency = amountMatch ? amountMatch[2] : "PKR";

                                const typeLabel = isExchange ? "Exchange" : isRefund ? "Refund" : "Penalty";
                                const badgeColor = isExchange ? "#92400e" : "#166534";
                                const badgeBg = isExchange ? "#fef3c7" : "#dcfce7";
                                const borderAccent = isExchange ? "#d97706" : "#16a34a";

                                const statusLabel = isExchange ? (amount === "0" ? "Changeable (Free)" : `Changeable (${currency} ${Number(amount).toLocaleString()})`) :
                                    (amount === "0" ? "Refundable (Free)" : `Refundable (${currency} ${Number(amount).toLocaleString()})`);

                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            borderRadius: "8px",
                                            p: "10px 14px",
                                            backgroundColor: "#f9fafb",
                                            border: "1px solid #e5e7eb",
                                            borderLeft: `4px solid ${borderAccent}`,
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "5px",
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <Box
                                                sx={{
                                                    fontSize: "10px",
                                                    fontWeight: "700",
                                                    px: "8px",
                                                    py: "2px",
                                                    borderRadius: "4px",
                                                    backgroundColor: badgeBg,
                                                    color: badgeColor,
                                                    textTransform: "uppercase",
                                                }}
                                            >
                                                {typeLabel}
                                            </Box>
                                            <Typography sx={{ fontSize: "12px", color: "#6b7280", fontWeight: "500" }}>
                                                {isBefore ? "Before Departure" : isAfter ? "After Departure" : "General"}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                fontWeight: "600",
                                                color: "#111827",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "4px",
                                            }}
                                        >
                                            <span style={{ color: borderAccent }}>✓</span> {statusLabel}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                    </DialogContent>

                    <Divider />

                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                        <Button variant="plain" color="neutral" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </ModalDialog>
            </Modal>
        );
    }

    // Default structure (Sabre Legacy)
    const passenger = fareData?.Summary?.PassengerDetails?.PassengerDetail
    const totalPrice = fareData?.Summary?.Total
    const baseFare = passenger?.PassengerFare?.BaseFare
    const equivFare = passenger?.PassengerFare?.EquivalentFare
    const commission = passenger?.PassengerFare?.Commission

    const getPenaltyStatus = (penalty) => {
        if (penalty.Type === "Refund") return "success"
        if (penalty.Type === "Exchange") return "warning"
        return "neutral"
    }

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog
                aria-labelledby="fare-modal-title"
                size="lg"
                sx={{
                    width: 600,
                    maxWidth: "95vw",
                    borderRadius: "md",
                    boxShadow: "lg",
                }}
            >
                <DialogTitle id="fare-modal-title" sx={{ pb: 1 }}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Typography level="h2" sx={{ fontSize: "lg", fontWeight: "lg" }}>
                            Fare Summary & Rules
                        </Typography>
                        <Button
                            variant="plain"
                            color="neutral"
                            size="sm"
                            onClick={onClose}
                            sx={{ borderRadius: "50%", minWidth: "auto" }}
                        >
                            <CloseIcon />
                        </Button>
                    </Box>
                </DialogTitle>

                <Divider sx={{ my: 1 }} />

                <DialogContent sx={{ maxHeight: "70vh", overflowY: "auto", px: 3 }}>
                    <Stack spacing={3}>
                        {/* Penalties & Conditions */}
                        <Box>
                            <Typography
                                level="h3"
                                sx={{
                                    fontSize: "md",
                                    fontWeight: "bold",
                                    mb: 1.5,
                                    textTransform: "uppercase",
                                    opacity: 0.7,
                                }}
                            >
                                Penalties & Conditions
                            </Typography>

                            <Stack spacing={1.5}>
                                {passenger?.PenaltiesInfo?.Penalty?.map((penalty, index) => (
                                    <Card
                                        key={index}
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: "var(--joy-palette-background-level1)",
                                            borderLeft:
                                                penalty.Type === "Refund"
                                                    ? "3px solid var(--joy-palette-success-500)"
                                                    : "3px solid var(--joy-palette-warning-500)",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                mb: 1,
                                            }}
                                        >
                                            <Box>
                                                <Chip size="sm" variant="solid" color={getPenaltyStatus(penalty)}>
                                                    {penalty.Type}
                                                </Chip>
                                                <Chip size="sm" variant="soft" sx={{ ml: 1 }}>
                                                    {penalty.Applicability} Departure
                                                </Chip>
                                            </Box>
                                            {penalty.Amount && (
                                                <Typography
                                                    level="body-md"
                                                    sx={{ fontWeight: "bold", color: "var(--joy-palette-primary-500)" }}
                                                >
                                                    {penalty.Amount} {penalty.CurrencyCode}
                                                </Typography>
                                            )}
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "flex",
                                                gap: 1,
                                                flexWrap: "wrap",
                                            }}
                                        >
                                            {penalty.Changeable === "true" && (
                                                <Chip size="sm" variant="plain" color="success">
                                                    ✓ Changeable
                                                </Chip>
                                            )}
                                            {penalty.Changeable === "false" && (
                                                <Chip size="sm" variant="plain" color="danger">
                                                    ✗ Not Changeable
                                                </Chip>
                                            )}
                                            {penalty.Refundable === "true" && (
                                                <Chip size="sm" variant="plain" color="success">
                                                    ✓ Refundable
                                                </Chip>
                                            )}
                                            {penalty.Refundable === "false" && (
                                                <Chip size="sm" variant="plain" color="danger">
                                                    ✗ Not Refundable
                                                </Chip>
                                            )}
                                            {penalty.ConditionsApply === "true" && (
                                                <Chip size="sm" variant="plain" color="neutral">
                                                    ⓘ Conditions Apply
                                                </Chip>
                                            )}
                                        </Box>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>

                <Divider />

                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, p: 2 }}>
                    <Button variant="plain" color="neutral" onClick={onClose}>
                        Close
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    )
}
